/* Seed data — §10. Loaded once on first run so the app is usable immediately.
   Prices quoted in the spec were the marks observed at capture, not cost basis,
   so only the average costs given there are recorded here. */

import type { CapBandRecord,
  AccountRecord, CapAssignment, CashBalance, Holding, Perp, Quote, Snapshot, ThemeRecord,
} from '../types';

/** The earliest date this book is known to have existed. The spec records no
    open date per lot, and guessing one would put invented data into the review
    clock, so every seeded holding shares this date until the user edits it. */
const SEED_DATE = '2026-06-03';

/** avgCost === 0 means "not recorded". Every seeded lot now has a real cost —
    the IBKR ones arrived from the position cards after §10 was written — but the
    convention stays, because a lot added later may not have one, and a missing
    cost has to stay missing rather than read as a free position. */
export const UNKNOWN_COST = 0;

type SeedLot = [ticker: string, shares: number, avgCost: number];

const REVOLUT: SeedLot[] = [
  ['ZETA', 668, 17.12],
  ['SILC', 159, 34.49],
  ['CRCL', 62, 64.45],
  ['MP', 54, 40.79],
  ['TE', 456, 5.94],
  ['MU', 2, 878.85],
  ['OUST', 40, 25.70],
  ['FLNC', 126, 14.02],
  ['TEM', 29, 45.54],
  ['SMR', 20, 8.85],
];

const XTB_IKE: SeedLot[] = [
  ['NRGV', 366, 3.60],
  ['HSAI', 208.6132, 21.04],
  ['MP', 25.1167, 40.14],
  ['OUST', 21.9302, 25.75],
  ['SILC', 5.0375, 30.07],
  ['TE', 42, 4.71],
];

const XTB_USD: SeedLot[] = [
  ['NRGV', 59, 3.58],
  ['HSAI', 19.8865, 16.41],
  ['ZETA', 128.1643, 16.79],
];

/* Read off the IBKR position cards on 2026-08-18, which is where the average
   costs §10 left blank finally came from. */
const IBKR: SeedLot[] = [
  ['CRCL', 2.6, 84.85],
  ['MU', 0.76, 915.96],
  ['ZETA', 4, 19.70],
];

/** Every seeded name is US-listed, so the Yahoo fetch symbol is the bare ticker
    — including SILC, which is Israeli but trades on Nasdaq (§3.2). Non-US names
    added later must carry their suffix (2498.HK, EL.PA, PZU.WA) on the record. */
function lots(account: Holding['account'], rows: SeedLot[]): Holding[] {
  return rows.map(([ticker, shares, avgCost]) => ({
    id: `seed-${account}-${ticker}`,
    ticker,
    yahooSymbol: ticker,
    account,
    shares,
    avgCost,
    currency: 'USD' as const,
    openedAt: SEED_DATE,
  }));
}

export const SEED_HOLDINGS: Holding[] = [
  ...lots('revolut', REVOLUT),
  ...lots('xtb_ike', XTB_IKE),
  ...lots('xtb_usd', XTB_USD),
  ...lots('ibkr', IBKR),
];

/** The five accounts §10 describes. More can be added in settings without
    touching this file. */
export const SEED_ACCOUNTS: AccountRecord[] = [
  { id: 'revolut', name: 'Revolut', currency: 'USD', order: 0 },
  { id: 'ibkr', name: 'IBKR', currency: 'USD', order: 1 },
  { id: 'xtb_usd', name: 'XTB USD', currency: 'USD', order: 2 },
  { id: 'xtb_ike', name: 'XTB IKE', currency: 'PLN', taxShielded: true, order: 3 },
  { id: 'mexc', name: 'MEXC', currency: 'USD', isFutures: true, order: 4 },
];

/* Free cash, as reported by the person holding it.
 *
 * The MEXC figure used to be derived: wallet total 2144.0079, less margin
 * posted 1422.2533, less the unrealised 687.8937, giving 33.8609. The chain is
 * arithmetically fine and the answer was wrong by 28.28 — the free balance is
 * 62.1417. Somewhere in the three figures it subtracted, one did not mean what
 * the derivation took it to mean.
 *
 * Which is the whole lesson: this book's own rule is that a figure you can be
 * told is never a figure to work out, and the derivation was a way of not
 * asking. All five of these are now stated rather than reconstructed, and the
 * spare cash on the exchange is stated first because it is the one that got
 * invented. */
export const SEED_CASH: CashBalance[] = [
  { account: 'mexc', amount: 62.1417, currency: 'USD' },
  { account: 'revolut', amount: 3.87, currency: 'USD' },
  { account: 'xtb_ike', amount: 2.40, currency: 'PLN' },
  { account: 'xtb_usd', amount: 0.08, currency: 'USD' },
  { account: 'ibkr', amount: 4.11, currency: 'USD' },
];

/* Three contracts open as of 2026-08-18 16:16. SKHY_USDT was closed earlier that
   afternoon and reopened at a different price — 161.15 against the old 162.97,
   on a smaller margin — so it is a new position, not the old one restored. No
   trade record is written for the close because its exit price is not in the
   record, and inventing one would invent a realized gain with it.

   sizeUsdt is the notional at entry — margin x leverage — not the "Size (USDT)"
   figure on the MEXC card, which is contracts x current mark and moves every
   tick. Checked against all three live cards: SKHY -6.1243, SKHYNIX -9.0331 and
   MU +701.8678 all reproduce to four decimals. */
export const SEED_PERPS: Perp[] = [
  {
    id: 'seed-perp-mu',
    // MEXC prints MU_USDT on the position card but answers to MUSTOCK_USDT on
    // the contract API — its equity perpetuals all carry that suffix. Same rule
    // as the Yahoo symbols in §3.2: store the exact fetch symbol, never derive
    // it from the display name at call time.
    symbol: 'MUSTOCK_USDT', display: 'MU', side: 'long', leverage: 5,
    sizeUsdt: 5178.8825, marginUsdt: 1035.7765,
    entryPrice: 839.36, liqPrice: 674.84, takeProfit: 1420,
    realizedUsdt: -1.3378, autoMarginAddition: true,
  },
  {
    id: 'seed-perp-skhynix',
    symbol: 'SKHYNIXSTOCK_USDT', display: 'SKHYNIX', side: 'long', leverage: 5,
    sizeUsdt: 847.8445, marginUsdt: 169.5689,
    entryPrice: 1153.53, liqPrice: 927.43,
    realizedUsdt: 0, autoMarginAddition: true,
  },
  {
    id: 'seed-perp-skhy',
    // Reopened 2026-08-18 at 161.15. Auto margin addition is off on this one,
    // unlike the other two — so it liquidates rather than reaching for the
    // spare cash, and the liquidation price below is the real line.
    symbol: 'SKHYSTOCK_USDT', display: 'SKHY', side: 'long', leverage: 5,
    sizeUsdt: 1084.5395, marginUsdt: 216.9079,
    entryPrice: 161.15, liqPrice: 129.59,
    realizedUsdt: 0, autoMarginAddition: false,
  },
];

/** Three known points to seed the chart (§10). Contributed capital is not known
    for these dates, so it is left at zero and the "gains only" series stays
    blank until the user enters cash flows in settings. */
export const SEED_SNAPSHOTS: Snapshot[] = [
  { at: '2026-06-03T12:00:00.000Z', totalUsd: 58040, contributedUsd: 0, seeded: true },
  { at: '2026-06-19T12:00:00.000Z', totalUsd: 55526, contributedUsd: 0, seeded: true },
  { at: '2026-08-18T12:00:00.000Z', totalUsd: 59405, contributedUsd: 0, seeded: true },
];

export const SEED_THEMES: ThemeRecord[] = [
  { name: 'AI capex', tickers: ['ZETA', 'SILC', 'HSAI', 'OUST', 'MU', 'TEM', 'FLNC', 'NRGV', 'TE', 'SMR', 'MP'] },
  { name: 'Semis and memory', tickers: ['MU', 'SILC'] },
  { name: 'Critical minerals and energy', tickers: ['MP', 'TE', 'FLNC', 'NRGV', 'SMR'] },
  { name: 'China', tickers: ['HSAI'] },
  { name: 'Physical AI sensing', tickers: ['OUST', 'HSAI'] },
];

/** §10 assigns a cap to six names. CRCL, MP, MU, TEM, SMR and NRGV are left
    without one on purpose — they are meant to surface as undocumented. */
export const SEED_CAPS: CapAssignment[] = [
  { ticker: 'ZETA', capKind: 'dominant' },
  { ticker: 'SILC', capKind: 'microCap' },
  { ticker: 'HSAI', capKind: 'midBandTop' },
  { ticker: 'OUST', capKind: 'midBandTop' },
  { ticker: 'FLNC', capKind: 'geoOverride' },
  { ticker: 'TE', capKind: 'preFcf' },
];

/** FX at capture (§10). Only USD/PLN is known at this point; EUR and HKD stay
    absent until the first live NBP table arrives, so a EUR or HKD listing reads
    as "no rate yet" rather than being converted at an invented one. */
export const SEED_FX = { usdPln: 3.714, publishedOn: '2026-08-18' };

/* Marks read off the broker on 2026-08-18 13:48 UTC, for the three names whose
   cards were to hand. They are seeded so those positions open on a real price
   rather than at cost, and they carry their true timestamp — the home screen
   will say how old they are, and the first successful quote fetch replaces them.
   The other nine names have no mark until the network provides one. */
export const SEED_QUOTES: Quote[] = [
  {
    symbol: 'CRCL', price: 72.12, previousClose: 74.59, currency: 'USD',
    exchangeName: 'NYSE', marketState: 'REGULAR', fetchedAt: '2026-08-18T13:48:00.000Z',
  },
  {
    symbol: 'ZETA', price: 28.93, previousClose: 28.83, currency: 'USD',
    exchangeName: 'NYSE', marketState: 'REGULAR', fetchedAt: '2026-08-18T13:48:00.000Z',
  },
  {
    symbol: 'MU', price: 963.91, previousClose: 1011.75, currency: 'USD',
    exchangeName: 'NASDAQ.NMS', marketState: 'REGULAR', shortName: 'MICRON TECHNOLOGY INC',
    fetchedAt: '2026-08-18T13:48:00.000Z',
  },
];

/* The nine ceilings the book ships with.
 *
 * They were a `const` object in `rules/limits.ts` and a union type built from
 * its keys, which is why there were exactly nine and why none could be renamed.
 * They are records now, seeded once and the user's to change — the same move
 * the five brokers made.
 *
 * **The ids are the originals, character for character.** Every stored
 * `CapAssignment`, the six assignments in §10 below, and every export anybody
 * holds names a band by one of these strings. Minting them from the display
 * names instead would give `top_conviction` and orphan all of it silently. They
 * are camelCase and `bandIdFromName` emits snake_case, so nothing typed later
 * can collide back on to one. */
export const SEED_CAP_BANDS: CapBandRecord[] = [
  { id: 'dominant', name: 'top conviction', pct: 35,
    reason: 'This is the one position allowed to dominate the book.' },
  { id: 'highBandTop', name: 'high conviction', pct: 10,
    reason: 'Nine or ten confluences line up behind this one.' },
  { id: 'midBandTop', name: 'medium conviction', pct: 5,
    reason: 'Seven or eight confluences line up behind this one.' },
  { id: 'lowBandTop', name: 'low conviction', pct: 3,
    reason: 'Five or six confluences line up behind this one.' },
  { id: 'geoOverride', name: 'geopolitical override', pct: 3,
    reason: 'The thesis rests on a competitor being excluded, which is a policy bet.' },
  { id: 'microCap', name: 'micro cap', pct: 1.5,
    reason: 'Daily volume is thin enough that your own size moves the exit price.' },
  { id: 'starterTop', name: 'starter', pct: 1,
    reason: 'Three or four confluences: a starter, not a holding yet.' },
  { id: 'preFcf', name: 'no free cash flow yet', pct: 1,
    reason: 'It has revenue but does not yet generate free cash flow.' },
  { id: 'preRevenue', name: 'pre-revenue', pct: 0.5,
    reason: 'It has no revenue yet, so the thesis is entirely forward-looking.' },
];
